import React from "react"
import {
  Thead, Tr, Th, Flex, Text, Box,
} from "@chakra-ui/react"
import { Table, flexRender } from "@tanstack/react-table"
import Icon from "components/elements/Icon"
import { LAST_NAME_ORDER_PREFIX } from "utilities/constants"
import { columnConfigs, getColumnLabel } from "./columnsConfig"
import { ExtendedGuestSummary } from "./helpers"

const SortIndicator = ({ direction }: { direction: "asc" | "desc" | false }) => (
  <Flex direction="column" align="center" pl={1.5} gap="2px">
    <Box
      as="svg"
      viewBox="0 0 8 5"
      w="8px"
      h="5px"
      color={direction === "asc" ? "orange.500" : "gray.300"}
      fill="currentColor"
    >
      <path d="M4 0L8 5H0L4 0Z" />
    </Box>
    <Box
      as="svg"
      viewBox="0 0 8 5"
      w="8px"
      h="5px"
      color={direction === "desc" ? "orange.500" : "gray.300"}
      fill="currentColor"
    >
      <path d="M4 5L0 0H8L4 5Z" />
    </Box>
  </Flex>
)

type TableMeta = {
  order?: string;
}

type ColumnMeta = {
  align?: string;
  icon?: string;
}

type Props = {
  table: Table<ExtendedGuestSummary>;
  getColumnWidth: (columnId: string) => string;
  cellPaddingX: number;
  headerHeightPx: number;
}

const DataTableHead = ({
  table,
  getColumnWidth,
  cellPaddingX,
  headerHeightPx,
}: Props) => (
  <Thead
    position="sticky"
    top={0}
    width="100%"
    bg="white"
    height={`${headerHeightPx}px`}
    zIndex={2}
    display="block"
    role="rowgroup"
  >
    {table.getHeaderGroups().map((headerGroup) => (
      <Tr
        key={headerGroup.id}
        display="flex"
        width="100%"
        borderBottomWidth="1px"
        borderBottomStyle="solid"
        borderBottomColor="gray.200"
        boxShadow="none"
        transition="box-shadow 0.15s ease"
        sx={{
          "[data-scrolled-top] &": {
            boxShadow: "0 2px 4px rgba(0,0,0,0.04)",
          },
        }}
        role="row"
      >
        {headerGroup.headers.map((header) => {
          if (!header.column.getIsVisible()) return null

          const isSortable = header.column.getCanSort() && header.column.id !== "select"
          const isActive = header.column.getIsSorted() !== false
          const meta = header.column.columnDef.meta as ColumnMeta | undefined
          const tableMeta = table.options.meta as TableMeta | undefined
          const isNameColumn = header.column.id === "name"
          const isSortedByLastName = isNameColumn
            && tableMeta?.order?.startsWith(LAST_NAME_ORDER_PREFIX)
          const canResize = header.column.getCanResize()

          return (
            <Th
              key={header.id}
              width={getColumnWidth(header.column.id)}
              minWidth={columnConfigs[header.column.id]?.minWidth}
              textTransform="none"
              fontWeight="bold"
              fontSize="sm"
              letterSpacing={0}
              cursor={isSortable ? "pointer" : "default"}
              onClick={isSortable ? header.column.getToggleSortingHandler() : undefined}
              color={isActive ? "orange.500" : "gray.700"}
              textAlign={meta?.align === "center" ? "center" : undefined}
              px={cellPaddingX}
              h={`${headerHeightPx}px`}
              py={0}
              display="flex"
              alignItems="center"
              flexShrink={0}
              boxSizing="border-box"
              role="columnheader"
              aria-sort={isActive ? (header.column.getIsSorted() === "desc" ? "descending" : "ascending") : undefined}
              position={isNameColumn ? "sticky" : "relative"}
              left={isNameColumn ? 0 : undefined}
              zIndex={isNameColumn ? 3 : undefined}
              bg="white"
              borderRightWidth="1px"
              borderRightColor="gray.100"
              transition="background 0.15s ease"
              _hover={isSortable ? { bg: "gray.50" } : undefined}
              sx={isNameColumn ? {
                "&::after": {
                  content: "\"\"",
                  position: "absolute",
                  top: 0,
                  right: 0,
                  width: "6px",
                  height: "100vh",
                  transform: "translateX(100%)",
                  background: "linear-gradient(to right, rgba(0,0,0,0.08), transparent)",
                  pointerEvents: "none",
                  opacity: 0,
                  transition: "opacity 0.15s ease",
                },
                "[data-scrolled-left] &::after": {
                  opacity: 1,
                },
              } : undefined}
            >
              <Flex align="center" width="100%">
                {meta?.icon && (
                  <Icon
                    icon={meta.icon}
                    size={4}
                    mr={2}
                    color={isActive ? "orange.500" : "gray.700"}
                  />
                )}
                {(() => {
                  const renderedHeader = flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )

                  if (typeof renderedHeader === "string" || typeof renderedHeader === "number") {
                    return (
                      <Text
                        whiteSpace="normal"
                        noOfLines={1}
                        as="span"
                      >
                        {getColumnLabel(header.column.id)}
                      </Text>
                    )
                  }

                  return renderedHeader
                })()}
                {isSortedByLastName && (
                  <Text as="span" fontWeight="normal" color="gray.500" fontSize="xs" ml={1} flexShrink={0}>(Last Name, First)</Text>
                )}

                {isSortable && (
                  <SortIndicator direction={header.column.getIsSorted()} />
                )}
              </Flex>

              {canResize && (
                <Box
                  position="absolute"
                  right={0}
                  top={0}
                  h="100%"
                  w="4px"
                  cursor="col-resize"
                  userSelect="none"
                  sx={{ touchAction: "none" }}
                  opacity={header.column.getIsResizing() ? 1 : 0}
                  bg={header.column.getIsResizing() ? "orange.500" : "gray.300"}
                  _hover={{ opacity: 1 }}
                  onMouseDown={header.getResizeHandler()}
                  onTouchStart={header.getResizeHandler()}
                  onDoubleClick={() => table.options.meta?.resetColumnSize(header.column.id)}
                  onClick={(e) => e.stopPropagation()}
                />
              )}
            </Th>
          )
        })}
      </Tr>
    ))}
  </Thead>
)

export default DataTableHead
